import { Button, Divider, Form, Row, Spin } from "antd";
import { FormItem } from "common/components/form/FormItem";
import ResponsiveCol from "common/components/form/ResponsiveCol";
import { useDateRange } from "context/DateRangeContext";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Search from "antd/es/input/Search";
import {
  EXPORT_REPORT_PERMISSION,
  getPagePermissions,
} from "security/permissions";
import useLoggedUser from "hooks/useLoggedUser";
import {
  downloadReceptionsReport,
  getReceptions,
  ResponseEntryDetail,
} from "api/generated";
import { useClient } from "hooks/useClient";
import DistributionPointSelected from "context/MyDistributionPoint";
import { querySerializer } from "common/helpers/queryUtils";
import ReceptionTable from "./components/ReceptionsTable";
import { usePaginationTable } from "hooks/commons/usePaginationTable";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { useTableSelection } from "hooks/commons/useTableSelection";
import { saveJsonToFile } from "routes/stockControl/components/jsonViewer";
import {
  DateFilterType,
  DateRangePicker,
} from "common/components/widgets/datePicker/DateRangePicker";
import { COMPLEMENT_PARENT } from "routes/cfdiComplements/ComplementsScreen";
import ComplementOptions from "common/components/complements/ComplementOptions";

function ReceptionScreen(props: RouteComponentProps) {
  const { client } = useClient();
  const { distributionPoint } = useContext(DistributionPointSelected);
  const [isLoading, setIsLoading] = useState(false);

  const selection = useTableSelection();
  const [filterCfdi, setFilterCfdi] = useState<boolean>();
  const pagination = usePaginationTable<ResponseEntryDetail>(props, {
    initialSort: ["endDate,DESC"],
    persistInUrl: true,
  });
  const [parentIdToDelete, setParentIdToDelete] = useState<string>();

  const query = useMemo(
    () =>
      pagination.debouncedFilterText.length > 1
        ? pagination.debouncedFilterText
        : undefined,
    [pagination.debouncedFilterText]
  );

  const { rangeTimeValue, setRangeTimeValue, getFormattedDates } =
    useDateRange();
  const { startDate, endDate } = getFormattedDates();
  const { permissions } = useLoggedUser();
  const exportReportsPermissions = getPagePermissions(
    EXPORT_REPORT_PERMISSION,
    permissions
  );

  function downloadReporte() {
    if (distributionPoint) {
      setIsLoading(true);
      downloadReceptionsReport({
        client,
        body: {
          inputRegisterStartDate: startDate,
          inputRegisterEndDate: endDate,
          query,
          distributionPointId: distributionPoint?.id,
          filterCfdi,
        },
        path: {
          type: "csv",
        },
      })
        .then(({ data }) => {
          saveJsonToFile(data, "text/csv", "receptions.csv");
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }

  const reloadTable = useCallback(() => {
    if (!distributionPoint) {
      return;
    }
    setIsLoading(true);
    getReceptions({
      client,
      querySerializer: querySerializer,
      query: {
        request: {
          inputRegisterStartDate: startDate,
          inputRegisterEndDate: endDate,
          query,
          distributionPointId: distributionPoint.id,
          filterCfdi: filterCfdi,
        },
        pageable: {
          page: pagination.currentPage,
          size: pagination.pageSize,
          sort: pagination.sort,
        },
      },
    })
      .then(({ data }) => {
        pagination.setTotalItems(data?.totalElements ?? 0);
        pagination.setItems(data?.content ?? []);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    client,
    startDate,
    endDate,
    query,
    distributionPoint,
    pagination.currentPage,
    pagination.pageSize,
    pagination.sort,
    filterCfdi,
  ]);

  // useEffect principal
  useEffect(() => {
    reloadTable();
  }, [reloadTable]);

  // Reset cuando cambian filtros principales
  useEffect(() => {
    if (props.location.search && props.location.search.length > 0) {
      return;
    }
  }, [distributionPoint?.id, startDate, endDate]);

  useEffect(() => {
    selection.resetSelected();
  }, [distributionPoint?.id, startDate, endDate]);

  return (
    <Spin size="large" tip="Cargando..." spinning={isLoading}>
      <div>
        <Form layout="vertical">
          <Row gutter={16} align="bottom">

            <ResponsiveCol>
              <FormItem label="Seleccionar periodo">
                <DateRangePicker
                  config={{
                    allowedTypes: [
                      DateFilterType.DAY,
                      DateFilterType.MONTH,
                      DateFilterType.RANGE,
                    ],
                    defaultType: DateFilterType.MONTH,
                    showQuickFilters: true,
                  }}
                  rangeTimeValue={rangeTimeValue}
                  setRangeTimeValue={(value) => {
                    pagination.resetPagination();
                    setRangeTimeValue(value);
                  }}
                />
              </FormItem>
            </ResponsiveCol>

            <ResponsiveCol>
              <FormItem label="Búsqueda por Folio Factura, Serie Factura, CFDI, Número de recepción">
                <Search
                  onChange={(event) => {
                    pagination.resetPagination();
                    pagination.setFilterText(event.target.value);
                  }}
                  value={pagination.filterText}
                />
              </FormItem>
            </ResponsiveCol>
            <ResponsiveCol>
              <FormItem>
                <Button
                  style={{ width: "100%" }}
                  onClick={downloadReporte}
                  disabled={
                    !exportReportsPermissions || pagination.totalItems === 0
                  }
                >
                  Descargar CSV
                </Button>
              </FormItem>
            </ResponsiveCol>
          </Row>
        </Form>

        <ComplementOptions
          selection={selection}
          reloadTable={reloadTable}
          parentType={COMPLEMENT_PARENT.RECEPCION}
          parentIdToDelete={parentIdToDelete}
          clearParentIdToDelete={() => {
            setParentIdToDelete(undefined);
          }}
        />

        <ReceptionTable
          selection={selection}
          isLoading={isLoading}
          reloadTable={reloadTable}
          pagination={pagination}
          setFilterCfdi={setFilterCfdi}
          deleteComplement={(record) => {
            setParentIdToDelete(record.id);
          }}
          location={props.location}
          history={props.history}
          match={props.match}
        />

        <Divider />
      </div>
    </Spin>
  );
}

export default withRouter(ReceptionScreen);
